import axios from 'axios';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import moment from 'moment/min/moment-with-locales';

import langApi from './api/langApi';
import authApi from './api/authApi';
import memberApi from './api/membershipApi';
// Fallback 리소스 import 추가
import enFallback from './locales/fallback/en.json';
import koFallback from './locales/fallback/ko.json';
import localStorageUtil from './utils/localStorageUtil';
import { normalizeLanguageCode } from './utils/language-util';

// 리소스 로딩 상태 추적
const isResourceLoading = false;
const resourceLoadError = null;

/**
 * 저장된 언어 정보를 가져옵니다.
 * localStorage에 저장된 언어 정보가 없으면 'en'을 반환
 */
const savedLanguage = getStoredLanguage() || 'en';

/**
 * i18n 초기화 설정
 * - 기본 언어: 저장된 언어 또는 'en'
 * - 보조 언어: 'en'
 * - 보간 설정: {변수} 형식으로 사용
 * - 리소스: 초기에는 빈 영어 번역만 포함
 * - 디버그 모드: 개발 환경에서만 활성화
 */
i18n.use(initReactI18next).init({
  lng: savedLanguage,
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
    prefix: '{',
    suffix: '}',
  },
  resources: {
    en: { translation: {} },
  },
  // 번역이 없을 때의 동작 설정
  returnNull: false,
  returnEmptyString: false,
  returnObjects: true,

  // lowercase
  lowerCaseLng: true,
});

/**
 * localStorage에서 저장된 언어 정보를 가져옵니다.
 * task-0320: 단일 키 languageInfo. JSON.parse 실패 시 null.
 * @returns {string | null} 저장된 언어 코드 또는 null
 */
function getStoredLanguage() {
  const languageInfo = localStorageUtil.getLanguageInfo();
  if (!languageInfo) return null;
  try {
    return JSON.parse(languageInfo).code;
  } catch {
    return null;
  }
}

// 🔥 Fallback 함수 추가
const getFallback = (languageCode: string) => {
  const code = languageCode.toLowerCase();
  if (code.startsWith('ko')) return koFallback;
  return enFallback;
};

/**
 * 사용자의 언어 설정을 처리
 * @param {string} [lang] 선택된 언어 코드 (선택사항)
 */
export async function setUserLanguage(lang?: string): Promise<void> {
  try {
    const brandCode = localStorageUtil.getBrandCode();
    const hasLocaleMessages =
      Object.keys(i18n.getDataByLanguage(i18n.language)?.translation || {}).length > 0;

    if (!hasLocaleMessages || lang) {
      const defaultLanguage = await getDefaultLanguage(brandCode, lang);
      await setLanguage(defaultLanguage);
    }
  } catch (error) {
    console.error('Error setting user language:', error);
  }
}

/**
 * 언어 및 방향을 설정
 * S3에서 번역 파일을 가져와 i18n에 적용
 * @param {Object} data 언어 설정 데이터
 * @param {string} data.code 언어 코드
 * @param {string} data.staticFileUrl S3의 번역 파일 URL
 * @param {boolean} [data.rtl] RTL 방향 여부
 * @returns {Promise<boolean>} 설정 성공 여부
 */
export const setLanguage = async (data: { code: string; staticFileUrl: string; rtl?: boolean }) => {
  try {
    const normalizedLang = normalizeLanguageCode(data.code);
    if (!data) localStorageUtil.removeToken();

    let messages;

    try {
      // S3에서 번역 파일 다운로드 시도
      const response = await axios.get(data.staticFileUrl);
      messages = response.data;
    } catch (downloadError) {
      console.warn('[i18n] Failed to download translation from S3, using fallback:', downloadError);
      // API 실패 시 정적 fallback 사용
      messages = getFallback(normalizedLang);
    }

    // 메시지 유효성 검사 (빈 객체 대비)
    if (!messages || typeof messages !== 'object' || Object.keys(messages).length === 0) {
      messages = getFallback(normalizedLang);
    }

    // 기존 리소스 제거
    i18n.removeResourceBundle(normalizedLang, 'translation');

    // 새로운 리소스 추가
    i18n.addResources(normalizedLang, 'translation', messages);

    // 언어 변경 전에 리소스가 로드되었는지 확인
    await i18n.changeLanguage(normalizedLang);

    // 리소스가 제대로 로드되었는지 확인
    const currentResources = i18n.getResourceBundle(normalizedLang, 'translation');

    if (!currentResources || Object.keys(currentResources).length === 0) {
      throw new Error('Translation resources not loaded properly');
    }

    // task-0320: 로그인 여부와 무관하게 단일 languageInfo에 저장 (#F3 #F4)
    const languageData = {
      code: normalizedLang,
      staticFileUrl: data.staticFileUrl,
      rtl: data.rtl ?? false,
    };
    localStorageUtil.setLanguageInfo(JSON.stringify(languageData));

    moment.locale(normalizedLang);
    document.documentElement.setAttribute('lang', normalizedLang);
    document.body.setAttribute('dir', data.rtl ? 'rtl' : 'ltr');

    return true;
  } catch (error) {
    console.error('Error setting language in i18n : ', error);

    // 🔥 최종 fallback - 영어 강제 적용
    try {
      const fallbackMessages = getFallback('en');
      i18n.removeResourceBundle('en', 'translation');
      i18n.addResources('en', 'translation', fallbackMessages);
      await i18n.changeLanguage('en');
      document.documentElement.setAttribute('lang', 'en');
      document.body.setAttribute('dir', 'ltr');
      return true;
    } catch (fallbackError) {
      console.error('[i18n] Even final fallback failed:', fallbackError);
      return false;
    }
  }
};

// 리소스 로딩 상태 확인
export const isTranslationsLoading = () => isResourceLoading;
export const getTranslationError = () => resourceLoadError;

/**
 * 토큰 유효성을 검증합니다.
 * 자동 로그인 중에는 토큰이 곧 정리될 수 있으므로 검증하지 않습니다. (#E2)
 * @param {boolean} isAutoLogin 자동 로그인 진행 여부
 * @returns {Promise<boolean>} 토큰 유효 여부
 */
const hasValidToken = async (isAutoLogin: boolean) => {
  const token = localStorageUtil.getToken();
  if (!token || isAutoLogin) return false;

  try {
    await authApi.validToken();
    return true;
  } catch {
    return false;
  }
};

/**
 * 로그인 계정의 option.pushLanguage를 언어팩에서 찾습니다. (#F4)
 * @param {Array} systemLanguageSet 언어팩 목록
 * @returns {Promise<Object|null>} 계정 언어 또는 null
 */
const findAccountLanguage = async (systemLanguageSet: { code: string }[]) => {
  try {
    const memberRes = await memberApi.getMemberInfo();
    const pushLanguage = memberRes?.data?.data?.option?.pushLanguage;
    if (typeof pushLanguage !== 'string' || !pushLanguage) return null;

    return systemLanguageSet.find((lang: { code: string }) => lang.code === pushLanguage) ?? null;
  } catch (error) {
    console.error('Error reading member pushLanguage:', error);
    return null;
  }
};

/**
 * 저장된 languageInfo를 언어팩에서 찾습니다. parse 실패 시 null. (#E1)
 * @param {Array} systemLanguageSet 언어팩 목록
 * @returns {Object|null} 저장된 언어 또는 null
 */
const findStoredLanguage = (systemLanguageSet: { code: string }[]) => {
  const storedLanguageInfo = localStorageUtil.getLanguageInfo();
  if (!storedLanguageInfo) return null;

  try {
    const { code } = JSON.parse(storedLanguageInfo);
    return systemLanguageSet.find((lang: { code: string }) => lang.code === code) ?? null;
  } catch {
    return null;
  }
};

/**
 * 기본 언어 설정 호출 함수
 *
 * 이 함수는 다음 순서로 언어를 결정:
 * 1. selectedLang이 제공된 경우: 해당 언어를 반환
 * 2. 로그인(토큰 유효): leftover languageInfo를 건너뛰고 GET /membership/member option.pushLanguage
 * 3. 비로그인: leftover languageInfo가 팩에 있으면 반환
 * 4. 위 조건이 모두 만족되지 않는 경우: 브랜드의 기본 언어를 반환 (#E7)
 *
 * 자동 로그인 중인 경우:
 * - 토큰 검증을 건너뛰고 guestLanguagePack API를 사용.
 * - 이는 자동 로그인 프로세스 중 토큰이 정리되는 경쟁 조건을 방지.
 *
 * @param {string | null} brandCode 브랜드 코드
 * @param {string} [selectedLang] 선택된 언어 코드 (선택사항)
 * @returns {Promise<Object|null>} 언어 설정 객체 또는 null
 */
export const getDefaultLanguage = async (brandCode: string | null, selectedLang?: string) => {
  // 자동 로그인 중인지 확인 : 자동 로그인 중에는 토큰 검증을 건너뛰어 경쟁 조건을 방지
  const isAutoLogin = window.location.search.includes('autoLogin=true');
  const isTokenValid = await hasValidToken(isAutoLogin);

  // 토큰 유효성에 따라 언어팩 API를 선택
  // - 인증된 사용자: getUserLanguageDefaultPack (인증 필요)
  // - 비인증 사용자 또는 자동 로그인 중: guestLanguagePack (인증 불필요)
  const fetchLanguagePack = isTokenValid
    ? langApi.getUserLanguageDefaultPack
    : langApi.guestLanguagePack;

  // 선택된 API로 언어팩 목록 가져오기
  const response = await fetchLanguagePack(brandCode ?? '');
  const systemLanguageSet = response?.data.data;

  if (!systemLanguageSet) return;

  // 1순위: selectedLang이 제공된 경우 해당 언어 반환
  if (selectedLang) {
    return systemLanguageSet.find((lang: { code: string }) => lang.code === selectedLang);
  }

  // 로그인: leftover languageInfo를 쓰지 않고 GET /membership/member option.pushLanguage (#F4)
  if (isTokenValid) {
    const accountLanguage = await findAccountLanguage(systemLanguageSet);
    if (accountLanguage) {
      return accountLanguage;
    }

    const { data } = await langApi.getUserLanguageDefault();
    return data.data;
  }

  // 비로그인 2순위: 단일 languageInfo. parse 실패 시 캠퍼스 fallback (#E1)
  const defaultLanguage = findStoredLanguage(systemLanguageSet);

  if (defaultLanguage) {
    return defaultLanguage;
  }

  // 비로그인 3순위: 캠퍼스/브랜드 기본 (#E7)
  const { data } = await langApi.guestLanguageDefault(brandCode ?? '');
  return data.data;
};

/**
 * 브랜드 코드에 해당하는 언어를 설정합니다.
 * @param {string} brandCode 브랜드 코드
 * @returns {Promise<boolean>} 설정 성공 여부
 */
export const getLanguage = async (brandCode: any) => {
  const defaultLangResult = await getDefaultLanguage(brandCode ?? '');
  const result = await setLanguage(defaultLangResult);
  return result;
};

export default i18n;
